import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, FeesSection, FilingsSection } from '@/components/public';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Check, 
  ChevronRight, 
  ChevronDown,
  FileText,
  Shield,
  Clock,
  DollarSign,
  Users,
  Building2,
  Scale,
  BadgeCheck,
  AlertCircle,
  Globe,
  Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_NAME = 'StartEase';
const BASE_URL = 'https://starteaseai.com';
const APP_URL = '/login';

// SEO overrides — exact title/description/canonical per service slug.
// Falls back to the dynamic title/description from the backend service record
// for any slug not listed here.
const SEO_OVERRIDES = {
  'llc': {
    title: 'Form a US LLC Online — All 50 States, 7–10 Days | StartEase Agent',
    description: 'Form your US LLC from anywhere in the world. No US trip needed. Registered agent included. EIN, operating agreement, and compliance support available.',
    canonical: 'https://starteaseai.com/usa/llc',
  },
  'c-corp': {
    title: 'Form a US C-Corporation — Investor-Ready Structure | StartEase Agent',
    description: 'Incorporate your C-Corp in Delaware or any US state. Preferred by VCs and angel investors. EIN, registered agent, and cap table support available.',
    canonical: 'https://starteaseai.com/usa/c-corp',
  },
  'ein': {
    title: 'US EIN Registration for Non-US Founders — 7 Business Days | StartEase Agent',
    description: 'Get your US Federal Tax ID (EIN) without a US trip. We file Form SS-4 by fax. 7 business days for non-US applicants. Required for banking, Stripe, and compliance.',
    canonical: 'https://starteaseai.com/usa/ein',
  },
  'registered-agent': {
    title: 'US Registered Agent Service — All 50 States | StartEase Agent',
    description: 'Maintain a legal US address for your business. We receive state mail, legal notices, and compliance documents on your behalf. Annual service, all 50 states.',
    canonical: 'https://starteaseai.com/usa/registered-agent',
  },
  'boi': {
    title: 'BOI Report Filing — FinCEN Beneficial Ownership (Foreign Entities) | StartEase Agent',
    description: 'Under the FinCEN final rule (effective 14 Aug 2026), domestic US companies are exempt from BOI reporting. Reporting now applies only to foreign-formed entities registered to do business in a US state. We handle those filings.',
    canonical: 'https://starteaseai.com/usa/boi',
  },
};


// Comprehensive content for each service
const SERVICE_CONTENT = {
  'llc': {
    heroSubtitle: 'The most flexible and popular business structure for entrepreneurs worldwide',
    entityType: 'llc',
    
    // What is section
    whatIs: {
      title: 'What is an LLC?',
      content: `A Limited Liability Company (LLC) is a hybrid business structure that combines the liability protection of a corporation with the tax benefits and operational flexibility of a partnership. Established under state law, an LLC creates a legal separation between the business and its owners (called "members"), protecting personal assets from business debts and lawsuits.

The LLC structure has become the most popular choice for small businesses, startups, and international entrepreneurs forming US companies. According to the IRS, over 35 million LLCs are registered in the United States, with hundreds of thousands of new formations each year.

Unlike corporations, LLCs are not required to hold annual meetings, maintain extensive corporate records, or follow rigid management structures. This flexibility, combined with "pass-through" taxation, makes LLCs an attractive option for business owners who want simplicity without sacrificing legal protection.`,
    },
    
    // Why form section
    whyForm: {
      title: 'Why Form an LLC?',
      subtitle: 'Key advantages that make LLCs the preferred choice for modern businesses',
      benefits: [
        {
          icon: 'Shield',
          title: 'Personal Asset Protection',
          description: 'Your personal assets (home, car, savings) are protected from business debts and lawsuits. Creditors can only pursue LLC assets, not your personal wealth.'
        },
        {
          icon: 'DollarSign',
          title: 'Tax Flexibility',
          description: 'LLCs enjoy "pass-through" taxation by default, avoiding double taxation. You can also elect to be taxed as an S-Corp or C-Corp for potential tax savings.'
        },
        {
          icon: 'Users',
          title: 'Management Flexibility',
          description: 'Choose between member-managed or manager-managed structures. No required board meetings, shareholder votes, or complex corporate formalities.'
        },
        {
          icon: 'FileText',
          title: 'Minimal Compliance',
          description: 'Less paperwork than corporations. No required annual meetings, board resolutions, or detailed minute-keeping in most states.'
        },
        {
          icon: 'Globe',
          title: 'Credibility & Trust',
          description: 'An LLC designation adds legitimacy to your business. Banks, vendors, and clients take your business more seriously with proper legal structure.'
        },
        {
          icon: 'Briefcase',
          title: 'Business Banking & Credit',
          description: 'Open business bank accounts, apply for business credit cards, and build business credit separate from your personal credit history.'
        }
      ]
    },
    
    // Requirements section
    requirements: {
      title: 'LLC Formation Requirements',
      subtitle: 'What you need to form your LLC',
      items: [
        {
          title: 'Unique Business Name',
          description: 'Your LLC name must be distinguishable from other registered businesses in your state. Must include "LLC" or "Limited Liability Company" designator.',
          required: true
        },
        {
          title: 'Registered Agent',
          description: 'A person or service with a physical address in the formation state to receive legal documents and official correspondence on behalf of your LLC.',
          required: true
        },
        {
          title: 'Principal Business Address',
          description: 'The main address where your business operates. Can be different from the registered agent address. PO Boxes may have restrictions.',
          required: true
        },
        {
          title: 'Member/Manager Information',
          description: 'Names and addresses of LLC members (owners) and/or managers. Some states allow anonymous LLCs with nominee services.',
          required: true
        },
        {
          title: 'Articles of Organization',
          description: 'The formation document filed with the state. We prepare this for you based on your business information.',
          required: true
        },
        {
          title: 'Operating Agreement',
          description: 'Internal document outlining ownership, profit distribution, and management structure. Required in some states, recommended for all.',
          required: false
        },
        {
          title: 'EIN (Federal Tax ID)',
          description: 'Required to open bank accounts, hire employees, and file taxes. We obtain this from the IRS on your behalf.',
          required: false
        }
      ]
    },
    
    // Step by step process
    process: {
      title: 'LLC Formation Process',
      subtitle: 'Simple 4-step process to form your LLC',
      steps: [
        {
          step: 1,
          title: 'Choose Your State & Name',
          description: 'Select your formation state (Wyoming and Delaware are popular for their business-friendly laws). We verify your desired business name is available.',
          timeline: 'Instant',
          details: [
            'Name availability search in your chosen state',
            'Guidance on state selection based on your business needs',
            'Name reservation if desired (optional)',
            'Compliance check for naming requirements'
          ]
        },
        {
          step: 2,
          title: 'Submit Your Information',
          description: 'Complete our streamlined online form with your business details. Takes approximately 10-15 minutes for most applicants.',
          timeline: '10-15 minutes',
          details: [
            'Business name and purpose',
            'Member/owner information',
            'Management structure selection',
            'Registered agent designation'
          ]
        },
        {
          step: 3,
          title: 'We Prepare & File',
          description: 'Our formation specialists prepare your Articles of Organization and file with the state. We monitor the filing until approval.',
          timeline: '1-2 business days',
          details: [
            'Articles of Organization preparation',
            'State filing fee payment',
            'Filing with Secretary of State',
            'Expedited processing if selected'
          ]
        },
        {
          step: 4,
          title: 'Receive Your Documents',
          description: 'Once approved, receive your complete formation package including approved documents, Operating Agreement, and EIN.',
          timeline: '3-10 business days',
          details: [
            'Approved Articles of Organization',
            'Custom Operating Agreement',
            'EIN confirmation letter',
            'Compliance calendar and next steps guide'
          ]
        }
      ]
    },
    
    // State fees table
    stateFees: [
      { state: 'Wyoming', code: 'WY', fee: 100, highlight: 'No State Income Tax', popular: true },
      { state: 'Delaware', code: 'DE', fee: 90, highlight: 'Business-Friendly Courts', popular: true },
      { state: 'Texas', code: 'TX', fee: 300, highlight: 'Large Market Access', popular: true },
      { state: 'Florida', code: 'FL', fee: 125, highlight: 'No Personal Income Tax', popular: true },
      { state: 'Nevada', code: 'NV', fee: 425, highlight: 'Strong Privacy Laws', popular: false },
      { state: 'California', code: 'CA', fee: 70, highlight: 'Tech & Innovation Hub', popular: false },
      { state: 'New York', code: 'NY', fee: 200, highlight: 'Financial Capital', popular: false },
      { state: 'Colorado', code: 'CO', fee: 50, highlight: 'Lowest Filing Fee', popular: false },
      { state: 'Washington', code: 'WA', fee: 200, highlight: 'No Income Tax', popular: false },
      { state: 'Georgia', code: 'GA', fee: 100, highlight: 'Southeast Business Hub', popular: false }
    ],
    
    // Who should form
    whoShouldForm: {
      title: 'Who Should Form an LLC?',
      subtitle: 'LLCs are ideal for a wide range of business types',
      categories: [
        { title: 'Freelancers & Consultants', description: 'Protect personal assets while working with clients' },
        { title: 'E-commerce Businesses', description: 'Amazon, Shopify, and online sellers' },
        { title: 'Real Estate Investors', description: 'Hold rental properties with liability protection' },
        { title: 'International Entrepreneurs', description: 'Non-US residents starting US businesses' },
        { title: 'Small Business Owners', description: 'Restaurants, retail stores, service businesses' },
        { title: 'Tech Startups (Early Stage)', description: 'Before seeking venture capital' },
        { title: 'Professional Services', description: 'Marketing agencies, design firms, IT services' },
        { title: 'Side Hustles', description: 'Part-time businesses needing protection' }
      ]
    },
    
    // Tax information
    taxInfo: {
      title: 'LLC Taxation Overview',
      subtitle: 'Understanding your tax options',
      content: `By default, single-member LLCs are taxed as sole proprietorships, and multi-member LLCs are taxed as partnerships. This "pass-through" taxation means profits flow directly to members' personal tax returns, avoiding the double taxation faced by C-Corporations.

However, LLCs have flexibility to elect different tax treatment:`,
      options: [
        {
          title: 'Default (Disregarded Entity / Partnership)',
          description: 'Profits pass through to personal tax returns. Simple filing, no corporate tax.',
          bestFor: 'Most small businesses and startups'
        },
        {
          title: 'S-Corporation Election',
          description: 'Can reduce self-employment taxes by splitting income between salary and distributions.',
          bestFor: 'Profitable businesses with $50K+ net income'
        },
        {
          title: 'C-Corporation Election',
          description: 'Corporate tax rates (21%) may be beneficial. Allows reinvestment at lower rates.',
          bestFor: 'High-growth businesses planning to raise capital'
        }
      ]
    },
    
    // Ongoing compliance
    compliance: {
      title: 'LLC Ongoing Compliance',
      subtitle: 'Keep your LLC in good standing',
      requirements: [
        {
          title: 'Annual Report',
          description: 'Most states require annual or biennial reports with updated business information.',
          frequency: 'Annual or Biennial',
          penalty: 'Varies by state ($25-$500+)'
        },
        {
          title: 'Registered Agent',
          description: 'Maintain a registered agent at all times. We provide this service for $150/year.',
          frequency: 'Continuous',
          penalty: 'Administrative dissolution risk'
        },
        {
          title: 'Franchise Tax',
          description: 'Some states (CA, DE, TX) charge annual franchise or gross receipts taxes.',
          frequency: 'Annual',
          penalty: 'Varies by state'
        },
        {
          title: 'Federal Tax Filing',
          description: 'File appropriate federal tax returns based on your tax election and membership structure.',
          frequency: 'Annual',
          penalty: 'IRS penalties and interest'
        }
      ]
    },
    
    // FAQs
    faqs: [
      {
        question: 'How long does it take to form an LLC?',
        answer: 'Formation time varies by state. Most states process LLC filings within 3-10 business days. Expedited processing (1-2 days) is available in many states for an additional fee. Wyoming and Delaware typically have the fastest processing times.'
      },
      {
        question: 'Can a non-US resident form an LLC?',
        answer: 'Yes! There are no citizenship or residency requirements to form an LLC in any US state. International entrepreneurs commonly form LLCs in Wyoming, Delaware, or Florida. You will need a registered agent with a US address, which we provide.'
      },
      {
        question: 'What is the difference between an LLC and a Corporation?',
        answer: 'LLCs offer more flexibility and simpler compliance than corporations. Corporations have rigid structures (board of directors, shareholders, officers) and require annual meetings and detailed records. LLCs have pass-through taxation by default, while C-Corps face double taxation. However, C-Corps are preferred for raising venture capital.'
      },
      {
        question: 'Do I need an Operating Agreement?',
        answer: 'While not required in all states, an Operating Agreement is highly recommended. It establishes ownership percentages, profit distribution, management structure, and procedures for adding/removing members. Banks often require it to open a business account.'
      },
      {
        question: 'Can I be my own Registered Agent?',
        answer: 'In most states, yes, if you have a physical address (not PO Box) in the formation state. However, this makes your personal address public record and requires you to be available during business hours to receive documents. Most business owners use a professional registered agent service for privacy and convenience.'
      },
      {
        question: 'What is the best state to form an LLC?',
        answer: 'It depends on your situation. If you have a physical presence in one state, forming there often makes sense. Wyoming offers no state income tax, low fees ($100), and strong privacy. Delaware has business-friendly courts and laws. If operating in California or New York, you may need to register there regardless of where you form.'
      },
      {
        question: 'How is an LLC taxed?',
        answer: 'By default, single-member LLCs are taxed as sole proprietorships (Schedule C), and multi-member LLCs as partnerships (Form 1065). Profits "pass through" to members\' personal returns. LLCs can elect S-Corp or C-Corp taxation if beneficial.'
      },
      {
        question: 'What is an EIN and do I need one?',
        answer: 'An EIN (Employer Identification Number) is like a Social Security Number for your business. You need an EIN to open a business bank account, hire employees, and file certain tax returns. Single-member LLCs without employees can technically use their SSN, but an EIN is recommended for privacy and professionalism.'
      },
      {
        question: 'Can I change my LLC to a Corporation later?',
        answer: 'Yes, you can convert an LLC to a Corporation through a process called "conversion" or "reorganization." The process and tax implications vary by state. Some businesses start as LLCs for simplicity and convert to C-Corps when seeking venture capital.'
      },
      {
        question: 'What ongoing requirements does an LLC have?',
        answer: 'Requirements vary by state but typically include: annual/biennial report filings, maintaining a registered agent, paying any applicable state taxes or fees, and filing federal tax returns. Some states like Wyoming have minimal requirements (just an annual report), while others like California have more obligations.'
      }
    ],
    
    // Related services
    relatedServices: [
      { slug: 'registered-agent', name: 'Registered Agent Service', description: 'Professional document handling in any state' },
      { slug: 'ein', name: 'EIN Application', description: 'Federal Tax ID for your LLC' },
      { slug: 'mail-forwarding', name: 'Mail Forwarding', description: 'US business address with mail scanning' }
    ],

    howItWorks: [
      { title: 'Select Jurisdiction', description: 'Choose the optimal state based on your business needs.' },
      { title: 'Provide Details', description: 'Submit business name and member information.' },
      { title: 'Document Filing', description: 'We prepare and file Articles of Organization.' },
      { title: 'Receive Documents', description: 'Get formation docs, Operating Agreement, and EIN.' }
    ]
  },
  
  'c-corp': {
    heroSubtitle: 'The preferred structure for startups seeking investment and unlimited growth potential',
    entityType: 'c-corp',
    
    whatIs: {
      title: 'What is a C-Corporation?',
      content: `A C-Corporation (C-Corp) is a legal business structure that creates a separate legal entity from its owners. C-Corps can raise capital by issuing stock, provide the strongest liability protection, and have perpetual existence regardless of ownership changes.

C-Corporations are the standard structure for companies planning to raise venture capital, go public, or scale to significant size. Nearly all publicly traded companies and most VC-backed startups are C-Corps, including tech giants like Apple, Google, and Amazon.

The "C" refers to Subchapter C of the Internal Revenue Code, which governs corporate taxation. Unlike LLCs and S-Corps, C-Corps pay corporate income tax on profits, and shareholders pay personal tax on dividends (often called "double taxation"). Despite this, the structure offers unmatched advantages for growth-oriented businesses.`
    },
    
    whyForm: {
      title: 'Why Form a C-Corporation?',
      subtitle: 'Key advantages that make C-Corps essential for growth-focused businesses',
      benefits: [
        {
          icon: 'DollarSign',
          title: 'Unlimited Investment Potential',
          description: 'Issue multiple classes of stock to raise venture capital, angel investment, or go public. No restrictions on number or type of shareholders.'
        },
        {
          icon: 'Users',
          title: 'Stock Options for Employees',
          description: 'Attract top talent with equity compensation including ISOs, NSOs, and RSUs. Standard compensation structure for tech companies.'
        },
        {
          icon: 'Shield',
          title: 'Strongest Liability Protection',
          description: 'Well-established legal precedent for corporate liability protection. Shareholders are protected from corporate debts and lawsuits.'
        },
        {
          icon: 'Building2',
          title: 'Perpetual Existence',
          description: 'Corporation continues regardless of ownership changes. Easy to transfer ownership through stock sales without disrupting operations.'
        },
        {
          icon: 'Globe',
          title: 'Global Recognition',
          description: 'International investors and partners understand and prefer the C-Corp structure. Standard for cross-border business.'
        },
        {
          icon: 'Scale',
          title: 'Delaware Advantage',
          description: 'Delaware Court of Chancery provides predictable, efficient resolution of corporate disputes. Preferred by investors.'
        }
      ]
    },
    
    requirements: {
      title: 'C-Corporation Formation Requirements',
      subtitle: 'What you need to incorporate',
      items: [
        {
          title: 'Corporate Name',
          description: 'Must include "Corporation," "Incorporated," "Company," or abbreviation. Must be distinguishable from existing entities.',
          required: true
        },
        {
          title: 'Registered Agent',
          description: 'Required in state of incorporation. We provide this service in all 50 states.',
          required: true
        },
        {
          title: 'Incorporator',
          description: 'Person who signs and files the Articles of Incorporation. We can serve as incorporator.',
          required: true
        },
        {
          title: 'Directors',
          description: 'At least one director required. Directors oversee major corporate decisions and appoint officers.',
          required: true
        },
        {
          title: 'Stock Structure',
          description: 'Number of authorized shares and par value. Delaware standard is 10 million authorized shares at $0.0001 par.',
          required: true
        },
        {
          title: 'Corporate Bylaws',
          description: 'Internal rules governing corporate operations. We provide customized bylaws.',
          required: true
        },
        {
          title: 'EIN (Federal Tax ID)',
          description: 'Required for tax filing, bank accounts, and payroll. We obtain this from the IRS.',
          required: false
        }
      ]
    },
    
    process: {
      title: 'C-Corporation Formation Process',
      subtitle: 'Professional incorporation in 4 steps',
      steps: [
        {
          step: 1,
          title: 'Choose State & Name',
          description: 'Select incorporation state (Delaware is the standard for startups). We verify your corporate name is available.',
          timeline: 'Instant',
          details: ['Name availability search', 'State selection guidance', 'Name reservation (optional)', 'DBA/trade name options']
        },
        {
          step: 2,
          title: 'Define Structure',
          description: 'Determine share structure, initial directors, and corporate purpose. We guide you through common configurations.',
          timeline: '15-20 minutes',
          details: ['Authorized shares setup', 'Director information', 'Registered agent selection', 'Corporate purpose']
        },
        {
          step: 3,
          title: 'File & Incorporate',
          description: 'We prepare and file your Articles of Incorporation with the state. Certificate issued upon approval.',
          timeline: '1-5 business days',
          details: ['Articles of Incorporation', 'State filing fee', 'Certificate of Incorporation', 'Expedited options available']
        },
        {
          step: 4,
          title: 'Complete Setup',
          description: 'Receive your complete corporate kit with bylaws, stock certificates, and organizational resolutions.',
          timeline: '3-10 business days',
          details: ['Corporate Bylaws', 'Initial resolutions', 'Stock certificates', 'EIN confirmation']
        }
      ]
    },
    
    stateFees: [
      { state: 'Delaware', code: 'DE', fee: 89, highlight: 'VC Standard', popular: true },
      { state: 'Wyoming', code: 'WY', fee: 100, highlight: 'No State Tax', popular: true },
      { state: 'Nevada', code: 'NV', fee: 425, highlight: 'Privacy Laws', popular: false },
      { state: 'Texas', code: 'TX', fee: 300, highlight: 'Large Market', popular: true },
      { state: 'California', code: 'CA', fee: 100, highlight: 'Tech Hub', popular: false },
      { state: 'New York', code: 'NY', fee: 125, highlight: 'Financial Hub', popular: false },
      { state: 'Florida', code: 'FL', fee: 70, highlight: 'No Income Tax', popular: true }
    ],
    
    whoShouldForm: {
      title: 'Who Should Form a C-Corporation?',
      subtitle: 'C-Corps are ideal for growth-oriented businesses',
      categories: [
        { title: 'VC-Backed Startups', description: 'Standard structure required by most investors' },
        { title: 'Companies Planning IPO', description: 'Required for public stock offerings' },
        { title: 'Tech Companies', description: 'Offering equity compensation to employees' },
        { title: 'Foreign Investment', description: 'Receiving investment from non-US sources' },
        { title: 'Multi-Founder Teams', description: 'Clear equity structure with vesting' },
        { title: 'Companies with Multiple Investors', description: 'Cap table management and governance' }
      ]
    },
    
    taxInfo: {
      title: 'C-Corporation Taxation',
      subtitle: 'Understanding corporate tax structure',
      content: `C-Corporations pay federal corporate income tax at a flat 21% rate (as of 2024). This is separate from personal income tax, which shareholders pay on dividends received.

This "double taxation" is often cited as a disadvantage, but it can be managed through:`,
      options: [
        {
          title: 'Retained Earnings',
          description: 'Reinvest profits at the 21% corporate rate instead of distributing as dividends.',
          bestFor: 'Growth companies reinvesting profits'
        },
        {
          title: 'Reasonable Salary',
          description: 'Pay shareholders reasonable salaries (deductible expense) instead of dividends.',
          bestFor: 'Active owners working in the business'
        },
        {
          title: 'QSBS Exclusion',
          description: 'Qualified Small Business Stock can exclude up to $10M in gains from federal tax.',
          bestFor: 'Founders and early investors'
        }
      ]
    },
    
    compliance: {
      title: 'C-Corporation Ongoing Compliance',
      subtitle: 'Maintain your corporate status',
      requirements: [
        {
          title: 'Annual Meeting',
          description: 'Hold at least one shareholders meeting and one board meeting per year.',
          frequency: 'Annual',
          penalty: 'Risk of piercing corporate veil'
        },
        {
          title: 'Meeting Minutes',
          description: 'Document major decisions in corporate minutes and resolutions.',
          frequency: 'As needed',
          penalty: 'Corporate governance issues'
        },
        {
          title: 'Annual Report',
          description: 'File annual report with state (Delaware requires Franchise Tax Report).',
          frequency: 'Annual',
          penalty: '$200+ late fees, dissolution risk'
        },
        {
          title: 'Delaware Franchise Tax',
          description: 'Due March 1 each year. Minimum $175/year for most startups.',
          frequency: 'Annual',
          penalty: '$200+ penalties'
        }
      ]
    },
    
    faqs: [
      {
        question: 'Why do VCs prefer Delaware C-Corps?',
        answer: 'Venture capitalists prefer Delaware C-Corps because: 1) Delaware has business-friendly corporate laws and specialized courts (Court of Chancery), 2) Standard legal documents and precedents reduce legal costs, 3) No state corporate income tax for companies operating outside Delaware, 4) VCs are familiar with Delaware governance.'
      },
      {
        question: 'What is the difference between C-Corp and S-Corp?',
        answer: 'C-Corps have no ownership restrictions and pay corporate tax. S-Corps have pass-through taxation but are limited to 100 US-person shareholders and one class of stock. VCs require C-Corps because S-Corps cannot have institutional or foreign investors.'
      },
      {
        question: 'How many shares should I authorize?',
        answer: 'Delaware standard is 10 million authorized shares at $0.0001 par value. This allows flexibility for future funding rounds, employee equity, and keeps Delaware franchise tax minimal (around $400/year). We set this up as part of your incorporation.'
      },
      {
        question: 'What is QSBS and how do I qualify?',
        answer: 'Qualified Small Business Stock (Section 1202) allows founders and early investors to exclude up to $10M (or 10x basis) of capital gains from federal tax. Requirements include: C-Corp status, under $50M in assets, and holding stock for 5+ years. Most tech startups qualify.'
      },
      {
        question: 'Can I convert my LLC to a C-Corp?',
        answer: 'Yes, LLCs can be converted to C-Corps through a statutory conversion or merger. This is common when raising VC funding. The process varies by state and has tax implications. We can guide you through this process.'
      },
      {
        question: 'What is the Delaware Franchise Tax?',
        answer: 'Delaware charges an annual franchise tax. For most startups, the minimum is $175/year using the Authorized Shares Method (up to 5,000 shares) or approximately $400/year for the standard 10 million share setup. Due March 1 annually.'
      },
      {
        question: 'Do I need to be in Delaware to incorporate there?',
        answer: 'No. You can incorporate in Delaware regardless of where you or your business are located. Most Delaware-incorporated companies operate elsewhere. You will need a registered agent in Delaware, which we provide.'
      },
      {
        question: 'What are the initial resolutions I need?',
        answer: 'Initial resolutions typically include: adopting bylaws, appointing officers, authorizing bank accounts, issuing initial shares, setting fiscal year, and approving equity incentive plan. We provide templates for all standard resolutions.'
      },
      {
        question: 'How do I issue stock to founders?',
        answer: 'After incorporation, founders sign stock purchase agreements at par value (usually $0.0001/share). Stock is subject to vesting (typically 4 years with 1-year cliff). 83(b) elections should be filed within 30 days to minimize taxes.'
      },
      {
        question: 'What is an 83(b) election?',
        answer: 'An 83(b) election is a tax filing that lets founders pay taxes on stock at its current (low) value rather than when it vests at a potentially higher value. Must be filed within 30 days of receiving restricted stock. Critical for founders.'
      }
    ],
    
    relatedServices: [
      { slug: 'registered-agent', name: 'Registered Agent Service', description: 'Delaware registered agent included' },
      { slug: 'ein', name: 'EIN Application', description: 'Federal Tax ID for your corporation' },
      { slug: 'llc', name: 'LLC Formation', description: 'Alternative structure for non-VC businesses' }
    ],

    howItWorks: [
      { title: 'Jurisdiction Selection', description: 'Delaware recommended for most corporations.' },
      { title: 'Share Structure', description: 'Define authorized shares and initial issuance.' },
      { title: 'Document Filing', description: 'We file Articles of Incorporation.' },
      { title: 'Corporate Kit', description: 'Receive bylaws, stock certificates, and EIN.' }
    ]
  },

  // Simpler content for other services
  's-corp': {
    heroSubtitle: 'Tax-efficient election for qualifying businesses',
    entityType: 'general',
    whatIs: {
      title: 'What is an S-Corporation?',
      content: `An S-Corporation is not a business structure but rather a tax election that LLCs or Corporations can make with the IRS. The "S" refers to Subchapter S of the Internal Revenue Code.

S-Corp election provides pass-through taxation (like an LLC) while allowing business owners to split income between salary and distributions, potentially reducing self-employment taxes.`
    },
    whyForm: {
      title: 'Why Elect S-Corp Status?',
      subtitle: 'Key advantages of S-Corp taxation',
      benefits: [
        { icon: 'DollarSign', title: 'Self-Employment Tax Savings', description: 'Only salary is subject to payroll taxes. Distributions avoid self-employment tax.' },
        { icon: 'Shield', title: 'Pass-Through Taxation', description: 'Profits pass to personal returns, avoiding C-Corp double taxation.' },
        { icon: 'FileText', title: 'Maintain LLC Flexibility', description: 'Keep LLC structure while gaining tax benefits of S-Corp election.' }
      ]
    },
    process: {
      title: 'S-Corp Election Process',
      subtitle: '4 steps to S-Corp status',
      steps: [
        { step: 1, title: 'Verify Eligibility', description: 'Confirm your entity qualifies for S-Corp election.', timeline: 'Instant', details: ['US entity', '100 or fewer shareholders', 'Only US residents/citizens', 'One class of stock'] },
        { step: 2, title: 'Prepare Form 2553', description: 'Complete IRS Form 2553 election.', timeline: '1 day', details: ['Shareholder consent', 'Tax year selection'] },
        { step: 3, title: 'File with IRS', description: 'Submit election to the IRS.', timeline: '1 day', details: ['IRS submission', 'Confirmation tracking'] },
        { step: 4, title: 'Receive Confirmation', description: 'IRS acceptance letter.', timeline: '4-8 weeks', details: ['Acceptance letter', 'Effective date confirmation'] }
      ]
    },
    faqs: [
      { question: 'When should I elect S-Corp status?', answer: 'Generally beneficial when net business income exceeds $40,000-$50,000 and you can pay yourself a reasonable salary. Consult a tax professional for your specific situation.' },
      { question: 'What is a "reasonable salary"?', answer: 'The IRS requires S-Corp shareholders who work in the business to pay themselves a reasonable salary based on industry standards and job duties before taking distributions.' },
      { question: 'Can international entrepreneurs use S-Corp?', answer: 'No. S-Corps can only have US citizens or residents as shareholders. Non-US persons should use LLC or C-Corp structure.' }
    ],
    howItWorks: [
      { title: 'Eligibility Review', description: 'Verify your entity qualifies for S-Corp election.' },
      { title: 'Form Preparation', description: 'We complete IRS Form 2553.' },
      { title: 'IRS Submission', description: 'File election with the Internal Revenue Service.' },
      { title: 'Confirmation', description: 'Receive IRS confirmation and payroll guidance.' }
    ]
  },
  
  'ein': {
    heroSubtitle: 'Federal Tax Identification Number for your business — 7 business days for international founders',
    entityType: 'general',
    speedBadge: {
      industry: '4+ weeks',
      ours: '7 business days',
      text: 'Industry standard: 4+ weeks for non-US founders. We deliver in 7 business days.'
    },
    whatIs: {
      title: 'What is an EIN?',
      content: `An Employer Identification Number (EIN), also known as a Federal Tax ID, is a unique 9-digit number assigned by the IRS to identify your business for tax purposes.

Think of it as a Social Security Number for your business. An EIN is required to open business bank accounts, hire employees, file business tax returns, and apply for business credit.

For US residents with a Social Security Number (SSN), EINs can be obtained instantly online. However, for international entrepreneurs without an SSN, the traditional IRS process takes 4-6 weeks via fax or mail. Our expedited service delivers your EIN in just 7 business days.`
    },
    whyForm: {
      title: 'Why You Need an EIN',
      subtitle: 'Essential for any US business',
      benefits: [
        { icon: 'Building2', title: 'Open Business Bank Accounts', description: 'Required by virtually all banks to open a business checking or savings account.' },
        { icon: 'Users', title: 'Hire Employees', description: 'Mandatory to pay employees and report payroll taxes to the IRS.' },
        { icon: 'Shield', title: 'Protect Personal SSN', description: 'Use your EIN instead of SSN on business documents for privacy.' },
        { icon: 'FileText', title: 'File Business Taxes', description: 'Required for partnership and corporate tax returns.' },
        { icon: 'Clock', title: '7 Business Days (Non-US)', description: 'Industry takes 4+ weeks for international applicants. We deliver in 7 business days.' },
        { icon: 'Globe', title: 'No SSN Required', description: 'International entrepreneurs can obtain an EIN without a US Social Security Number.' }
      ]
    },
    process: {
      title: 'EIN Application Process',
      subtitle: 'Fast and simple — 7 business days for international founders',
      steps: [
        { step: 1, title: 'Provide Information', description: 'Business name, address, and responsible party details.', timeline: '5 minutes', details: ['Entity type', 'Formation date', 'Principal activity', 'Responsible party info'] },
        { step: 2, title: 'We Complete SS-4', description: 'IRS Form SS-4 application preparation with accuracy verification.', timeline: '1 hour', details: ['Form completion', 'Accuracy verification', 'IRS compliance check'] },
        { step: 3, title: 'Expedited IRS Submission', description: 'We use our established IRS relationship for faster processing.', timeline: '1-2 business days', details: ['Priority IRS channel', 'Real-time tracking', 'Dedicated support'] },
        { step: 4, title: 'Receive EIN', description: 'EIN confirmation letter delivered. 7 business days for non-US applicants.', timeline: '7 business days (non-US)', details: ['Same-day for US applicants with SSN', '7 business days for international', 'IRS confirmation letter'] }
      ]
    },
    faqs: [
      { question: 'How long does it take to get an EIN?', answer: 'For US applicants with SSN, EINs can be obtained same-day. For international applicants without SSN, the standard IRS process takes 4-6 weeks. Our expedited service delivers in just 7 business days for international founders.' },
      { question: 'Can a non-US resident get an EIN?', answer: 'Yes! International applicants without SSN/ITIN can absolutely obtain an EIN. While the IRS standard process takes 4-6 weeks for international applicants, our service delivers in 7 business days.' },
      { question: 'Why is EIN faster with your service?', answer: 'We have established processes and dedicated channels with the IRS. Our team handles all the paperwork correctly the first time, avoiding delays from errors or rejections that commonly occur with self-filing.' },
      { question: 'Is an EIN free from the IRS?', answer: 'Yes, the IRS does not charge a fee for EIN applications. Our service fee covers expedited preparation, filing, follow-up, and support to ensure you receive your EIN in 7 business days.' },
      { question: 'What documents do I need?', answer: 'You need: your passport or government ID, your company formation documents (Articles of Organization or Incorporation), and basic business information (address, activity type).' },
      { question: 'Can I apply for EIN before forming my company?', answer: 'No. You must first form your LLC or Corporation before applying for an EIN. The IRS requires your company to legally exist with an official formation date.' }
    ],
    howItWorks: [
      { title: 'Entity Information', description: 'Provide company details and responsible party.' },
      { title: 'SS-4 Preparation', description: 'We complete the IRS EIN application.' },
      { title: 'Expedited Filing', description: 'Priority submission to IRS.' },
      { title: '7-Day Delivery', description: 'Receive EIN in 7 business days (non-US).' }
    ]
  },
  
  'registered-agent': {
    heroSubtitle: 'Professional document handling in any US jurisdiction',
    entityType: 'general',
    whatIs: {
      title: 'What is a Registered Agent?',
      content: `A Registered Agent (also called Statutory Agent or Agent for Service of Process) is a person or company designated to receive legal documents, government notices, and official correspondence on behalf of your business.

Every LLC and Corporation must have a registered agent with a physical address in the state of formation. The registered agent must be available during normal business hours to accept service of process.`
    },
    whyForm: {
      title: 'Why Use Professional Registered Agent Service?',
      subtitle: 'Protect your privacy and ensure compliance',
      benefits: [
        { icon: 'Shield', title: 'Privacy Protection', description: 'Keep your personal address off public records. Our address appears on state filings.' },
        { icon: 'Clock', title: 'Never Miss Documents', description: 'Professional availability during business hours. Immediate notification when documents arrive.' },
        { icon: 'Globe', title: 'Any State Coverage', description: 'Single point of contact for multi-state businesses. We cover all 50 states.' },
        { icon: 'FileText', title: 'Compliance Support', description: 'Annual report reminders and compliance calendar to keep your business in good standing.' }
      ]
    },
    process: {
      title: 'How It Works',
      subtitle: 'Simple setup process',
      steps: [
        { step: 1, title: 'Select State', description: 'Choose the state(s) where you need registered agent service.', timeline: 'Instant', details: ['All 50 states available'] },
        { step: 2, title: 'We Register', description: 'We file as your official registered agent with the state.', timeline: '1-3 days', details: ['State notification', 'Official designation'] },
        { step: 3, title: 'Documents Received', description: 'All legal documents delivered to our secure facility.', timeline: 'Ongoing', details: ['Service of process', 'State notices', 'Tax documents'] },
        { step: 4, title: 'Instant Forwarding', description: 'Same-day scanning and notification to your dashboard.', timeline: 'Same day', details: ['Email notification', 'Digital copies', 'Physical forwarding option'] }
      ]
    },
    faqs: [
      { question: 'Can I be my own registered agent?', answer: 'Yes, if you have a physical address (not PO Box) in the state and are available during business hours. However, this makes your address public and requires consistent availability.' },
      { question: 'What happens if I miss a legal document?', answer: 'Missing service of process can result in default judgments against your business. Our service ensures documents are received and forwarded immediately.' },
      { question: 'Do I need registered agent in each state I operate?', answer: 'Yes. If your business is registered (qualified) in multiple states, you need a registered agent in each state.' }
    ],
    howItWorks: [
      { title: 'State Selection', description: 'Choose your business registration state.' },
      { title: 'Agent Designation', description: 'We become your official registered office.' },
      { title: 'Document Receipt', description: 'All legal notices delivered to our facility.' },
      { title: 'Instant Forwarding', description: 'Same-day scanning to your secure dashboard.' }
    ]
  },
  
  'mail-forwarding': {
    heroSubtitle: 'Professional US business address with mail handling services',
    entityType: 'general',
    whatIs: {
      title: 'What is Mail Forwarding Service?',
      content: `Mail Forwarding provides your business with a professional US street address where all business mail is received, scanned, and forwarded to you anywhere in the world.

This service is essential for international entrepreneurs, remote businesses, and anyone wanting a professional address without a physical office. Your address can be used on business cards, websites, and official documents.`
    },
    whyForm: {
      title: 'Why Use Mail Forwarding?',
      subtitle: 'Professional presence without physical office',
      benefits: [
        { icon: 'Globe', title: 'US Business Address', description: 'Professional street address in prime US locations. Not a PO Box.' },
        { icon: 'FileText', title: 'Mail Scanning', description: 'All mail scanned and uploaded to your dashboard within 24 hours.' },
        { icon: 'Clock', title: 'Global Forwarding', description: 'Physical mail forwarded to any address worldwide.' },
        { icon: 'Shield', title: 'Privacy Protection', description: 'Keep your home address private. Use business address on all documents.' }
      ]
    },
    faqs: [
      { question: 'Can I use this address for LLC formation?', answer: 'This address can be used as your business address on most documents. However, registered agent address has different requirements and must be separate service.' },
      { question: 'How quickly is mail scanned?', answer: 'Standard mail is scanned within 24 hours of receipt. You receive email notification when new mail is available in your dashboard.' },
      { question: 'Can I receive packages?', answer: 'Yes, we accept packages from all major carriers (USPS, UPS, FedEx). Package forwarding is available for an additional fee based on size and destination.' }
    ],
    howItWorks: [
      { title: 'Address Selection', description: 'Obtain professional US business address.' },
      { title: 'Mail Receipt', description: 'All business mail delivered to our facility.' },
      { title: 'Scan & Forward', description: 'Same-day digital delivery or physical forwarding.' },
      { title: 'Online Management', description: 'Manage all mail from your dashboard.' }
    ]
  },

  'boi': {
    heroSubtitle: 'Beneficial Ownership Information — now required only for foreign-formed entities registered in the US',
    entityType: 'general',
    whatIs: {
      title: 'What is a BOI Report?',
      content: `The Beneficial Ownership Information (BOI) Report is a federal filing to the Financial Crimes Enforcement Network (FinCEN) under the Corporate Transparency Act (CTA).

Important update: under FinCEN's final rule published on 14 August 2026, all domestic US companies (and US persons) are permanently exempt from BOI reporting — no domestic LLC or corporation needs to file an initial, updated or corrected report. FinCEN is also removing previously submitted data on US persons and domestic entities.

Reporting now applies only to "foreign reporting companies" — entities formed under the law of a foreign country that have registered to do business in a US state or tribal jurisdiction. Even then, they do not have to report the information of any US-person beneficial owner or company applicant; they report the company's own details and their non-US beneficial owners.`
    },
    whyForm: {
      title: 'Who still needs to file',
      subtitle: 'Foreign-formed entities registered to do business in the US',
      benefits: [
        { icon: 'Building2', title: 'Foreign entities only', description: 'Companies formed abroad that register to do business in a US state must file. Domestic US LLCs and corporations are exempt.' },
        { icon: 'Shield', title: 'US owners excluded', description: 'A foreign reporting company does not report the BOI of any US-person beneficial owner or company applicant.' },
        { icon: 'Clock', title: 'Deadline for new registrations', description: 'A newly registered foreign reporting company generally files within 30 days of its US registration taking effect.' },
        { icon: 'FileText', title: 'We handle the filing', description: 'If your foreign entity is in scope, we prepare and submit the report to FinCEN and track confirmation.' }
      ]
    },
    requirements: {
      title: 'BOI Filing Requirements',
      subtitle: 'Information needed for your report',
      items: [
        { title: 'Company Information', description: 'Legal name, trade names/DBAs, current US address, state of formation, and EIN.', required: true },
        { title: 'Beneficial Owner Details', description: 'Full legal name, date of birth, current residential address for each beneficial owner.', required: true },
        { title: 'ID Document', description: 'US passport, state ID/drivers license, or foreign passport for each beneficial owner.', required: true },
        { title: 'Company Applicant Info', description: 'For companies formed after January 1, 2024: information about who filed the formation documents.', required: false },
        { title: 'FinCEN Identifier', description: 'Optional unique ID that can be used instead of personal information for privacy.', required: false }
      ]
    },
    process: {
      title: 'BOI Filing Process',
      subtitle: 'Simple 4-step compliance',
      steps: [
        { step: 1, title: 'Gather Information', description: 'Collect required details about your company and all beneficial owners.', timeline: '15-30 minutes', details: ['Company legal name and address', 'EIN or Tax ID', 'Beneficial owner names and addresses', 'ID documents for each owner'] },
        { step: 2, title: 'Submit to Us', description: 'Provide information through our secure platform. We verify completeness.', timeline: '10 minutes', details: ['Secure document upload', 'Information verification', 'Compliance review'] },
        { step: 3, title: 'We File with FinCEN', description: 'Our team submits your BOI Report to FinCEN through the official system.', timeline: '1-2 business days', details: ['FinCEN BOIR system submission', 'Confirmation tracking', 'Error resolution if needed'] },
        { step: 4, title: 'Receive Confirmation', description: 'Get your filing confirmation and compliance certificate for your records.', timeline: 'Same day', details: ['FinCEN confirmation', 'Compliance certificate', 'Update reminder service'] }
      ]
    },
    compliance: {
      title: 'BOI Compliance Timeline',
      subtitle: 'Applies to foreign reporting companies only',
      requirements: [
        { title: 'Domestic US companies', description: 'Permanently exempt under the FinCEN final rule effective 14 August 2026. No initial, updated or corrected BOI report is required.', frequency: 'No filing required', penalty: 'None' },
        { title: 'Foreign entity registered before 26 Mar 2025', description: 'Foreign reporting companies already registered had an initial deadline set under the interim rule.', frequency: 'Initial deadline has passed (was 25 Apr 2025)', penalty: 'Enforcement per FinCEN guidance' },
        { title: 'Foreign entity registering after 26 Mar 2025', description: 'Files within 30 days of receiving notice that its US registration is effective.', frequency: '30 days from effective registration', penalty: 'Enforcement per FinCEN guidance' },
        { title: 'US-person owners', description: 'A foreign reporting company does not report the information of any US-person beneficial owner or company applicant.', frequency: 'Not reported', penalty: 'N/A' },
      ]
    },
    faqs: [
      { question: 'Do domestic US companies still need to file a BOI report?', answer: 'No. Under FinCEN\'s final rule effective 14 August 2026, all domestic US companies and US persons are permanently exempt from BOI reporting. You do not need to file an initial, updated or corrected report, and FinCEN is removing previously submitted domestic data.' },
      { question: 'Who still has to file a BOI report?', answer: 'Only "foreign reporting companies" — entities formed under the law of a foreign country that have registered to do business in a US state or tribal jurisdiction. They report their own details and any non-US-person beneficial owners; they do not report US-person owners or applicants.' },
      { question: 'Who is a beneficial owner?', answer: 'For a foreign reporting company that is in scope, a beneficial owner is generally an individual who directly or indirectly owns 25% or more of the company or exercises substantial control over it. US-person beneficial owners are not reported.' },
      { question: 'What is the deadline for a foreign reporting company?', answer: 'A foreign entity that registers to do business in the US after 26 March 2025 generally files within 30 days of receiving notice that its US registration is effective.' },
      { question: 'Is BOI information public?', answer: 'No. BOI information is stored securely by FinCEN and is only accessible to authorized government agencies, financial institutions (with customer consent) and certain foreign authorities.' },
      { question: 'Do state-level ownership rules still apply?', answer: 'Possibly. The federal exemption for domestic companies does not remove separate state requirements — for example, New York\'s LLC Transparency Act — so check the rules in your state of formation.' }
    ],
    howItWorks: [
      { title: 'Gather Information', description: 'Collect company and beneficial owner details.' },
      { title: 'Submit Details', description: 'Provide information through our secure platform.' },
      { title: 'FinCEN Filing', description: 'We submit your report to FinCEN.' },
      { title: 'Confirmation', description: 'Receive filing confirmation and compliance certificate.' }
    ]
  }
};

const POPULAR_STATES = [
  { slug: 'wyoming', name: 'Wyoming', code: 'WY' },
  { slug: 'delaware', name: 'Delaware', code: 'DE' },
  { slug: 'texas', name: 'Texas', code: 'TX' },
  { slug: 'florida', name: 'Florida', code: 'FL' },
  { slug: 'nevada', name: 'Nevada', code: 'NV' },
  { slug: 'california', name: 'California', code: 'CA' }
];

const TRUST_STATS = [
  { value: '10,000+', label: 'Businesses Formed' },
  { value: '50', label: 'States Covered' },
  { value: '98%', label: 'Customer Satisfaction' },
  { value: '24/7', label: 'Support Available' }
];

// FAQ Accordion Component
const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div key={index} className="bg-[#111c17] border border-white/10" data-testid={`faq-item-${index}`}>
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
          >
            <span className="font-medium text-white">{faq.question}</span>
            <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
          </button>
          {openIndex === index && (
            <div className="px-6 pb-5">
              <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Icon component mapper
const IconMap = {
  Shield, DollarSign, Users, FileText, Globe, Briefcase, Building2, Scale, BadgeCheck, AlertCircle, Clock, Check
};

const USAServicePage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // S-Corp is coming soon - disable CTAs
  const isComingSoon = slug === 's-corp';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const serviceRes = await axios.get(`${API}/public/services/${slug}`);
        setService(serviceRes.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-emerald-500/40 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !service) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200">
        <Header />
        <div className="flex items-center justify-center py-32">
          <SEOHead title="Service Not Found" siteName={SITE_NAME} noindex={true} />
          <div className="text-center px-4">
            <h1 className="font-serif text-3xl font-semibold text-white mb-4">Service Not Found</h1>
            <p className="text-slate-400 mb-8">The requested service could not be located.</p>
            <Link to="/usa/" className="btn-corporate inline-flex items-center gap-2">
              View Services <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const content = SERVICE_CONTENT[slug] || SERVICE_CONTENT['llc'];

  return (
    <div className="min-h-screen bg-[#0b1310] text-slate-200" data-testid="usa-service-page">
      <SEOHead
        title={SEO_OVERRIDES[slug]?.title ?? `${service.name} | Professional Business Formation Services`}
        description={SEO_OVERRIDES[slug]?.description ?? (service.meta_description || service.description)}
        siteName={SITE_NAME}
        canonical={SEO_OVERRIDES[slug]?.canonical ?? `${BASE_URL}/usa/${slug}/`}
      />

      <Header />

      {/* Hero Section - StartEase Agent Style */}
      <section className="bg-[#1e293b] relative" data-testid="service-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <nav className="mb-10" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-slate-400">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to="/usa/" className="hover:text-white transition-colors">Services</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-emerald-400 font-medium">{service.name}</li>
            </ol>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-6">
                Business Formation
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1] mb-8">
                {service.name}
              </h1>
              <p className="text-xl text-slate-400 leading-relaxed mb-6">
                {content.heroSubtitle}
              </p>
              
              {/* Speed Badge for EIN */}
              {content.speedBadge && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 mb-8 inline-block" data-testid="speed-badge">
                  <p className="text-white font-medium text-sm">
                    <span className="text-slate-400">Industry: {content.speedBadge.industry}</span>
                    {' → '}
                    <span className="text-emerald-400 font-semibold">We deliver: {content.speedBadge.ours}</span>
                  </p>
                </div>
              )}
              
              <div className="flex flex-wrap gap-8 text-sm text-slate-400 mb-10">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>From <span className="text-white font-medium">${service.base_price}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>{service.timeline_days || '3-10 Business Days'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Expert Support Included</span>
                </div>
              </div>
              {isComingSoon ? (
                <span className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500/15 text-emerald-400 font-medium cursor-not-allowed" data-testid="service-hero-cta">
                  Coming Soon
                </span>
              ) : (
                <a href={APP_URL} className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors" data-testid="service-hero-cta">
                  Get Started <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              )}
            </div>
            
            {/* Features Card */}
            <div className="bg-slate-800 border border-slate-700 p-10" data-testid="service-features-card">
              <h2 className="text-xl font-semibold text-white mb-8">What's Included</h2>
              <ul className="space-y-4">
                {(service.features || []).slice(0, 8).map((feature, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Service Fee</span>
                  <span className="text-3xl font-bold text-white">${service.base_price}</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">+ state filing fees</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Bar */}
        <div className="border-t border-slate-700">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {TRUST_STATS.map((stat, index) => (
                <div key={index} className="text-center md:text-left">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      {content.whatIs && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="service-what-is">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-4">Overview</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-8">
              {content.whatIs.title}
            </h2>
            <div className="prose prose-lg max-w-none">
              {content.whatIs.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-slate-400 leading-relaxed mb-6">{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Form Section */}
      {content.whyForm && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="service-why-form">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Benefits</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                {content.whyForm.title}
              </h2>
              <p className="text-xl text-slate-400">{content.whyForm.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {content.whyForm.benefits.map((benefit, index) => {
                const IconComponent = IconMap[benefit.icon] || Check;
                return (
                  <div key={index} className="bg-[#111c17] border border-white/10 p-8">
                    <div className="w-12 h-12 bg-[#111c17] flex items-center justify-center mb-6">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                    <p className="text-slate-400">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Requirements Section */}
      {content.requirements && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="service-requirements">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Requirements</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                {content.requirements.title}
              </h2>
              <p className="text-xl text-slate-400">{content.requirements.subtitle}</p>
            </div>
            
            <div className="space-y-4">
              {content.requirements.items.map((item, index) => (
                <div key={index} className="bg-[#070d0b] border border-white/10 p-6 flex items-start gap-4">
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${item.required ? 'bg-[#111c17] text-white' : 'bg-[#111c17] text-slate-400'}`}>
                    {item.required ? <Check className="w-4 h-4" /> : <span className="text-xs">OPT</span>}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process Section */}
      {content.process && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="service-process">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Process</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                {content.process.title}
              </h2>
              <p className="text-xl text-slate-400">{content.process.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {content.process.steps.map((step, index) => (
                <div key={index} className="bg-[#111c17] border border-white/10 p-8" data-testid={`service-step-${step.step}`}>
                  <div className="flex items-start gap-6">
                    <div className="text-5xl font-serif font-semibold text-slate-300">{step.step}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                        <span className="text-xs bg-[#111c17] text-slate-400 px-2 py-1">{step.timeline}</span>
                      </div>
                      <p className="text-slate-400 mb-4">{step.description}</p>
                      {step.details && (
                        <ul className="space-y-2">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                              <Check className="w-4 h-4 text-white" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* State Fees Table */}
      {content.stateFees && ['llc', 'c-corp'].includes(slug) && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="service-state-fees">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">State Filing Fees</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                Available in All 50 States
              </h2>
              <p className="text-xl text-slate-400">Government filing fees vary by state</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border border-white/10">
                <thead>
                  <tr className="bg-[#070d0b]">
                    <th className="text-left px-6 py-4 font-semibold text-white border-b border-white/10">State</th>
                    <th className="text-left px-6 py-4 font-semibold text-white border-b border-white/10">Filing Fee</th>
                    <th className="text-left px-6 py-4 font-semibold text-white border-b border-white/10">Highlight</th>
                    <th className="text-right px-6 py-4 font-semibold text-white border-b border-white/10">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {content.stateFees.map((state, index) => (
                    <tr key={index} className={`${state.popular ? 'bg-[#070d0b]' : ''} border-b border-white/10 last:border-b-0`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-white">{state.code}</span>
                          <span className="text-slate-400">{state.state}</span>
                          {state.popular && <span className="text-xs bg-[#111c17] text-white px-2 py-0.5">Popular</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-semibold text-white">${state.fee}</td>
                      <td className="px-6 py-4 text-slate-400">{state.highlight}</td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/usa/states/${state.state.toLowerCase().replace(' ', '-')}/${slug}/`} className="text-white hover:text-emerald-400 font-medium text-sm inline-flex items-center gap-1">
                          Start <ArrowRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Who Should Form */}
      {content.whoShouldForm && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="service-who-should-form">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Ideal For</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                {content.whoShouldForm.title}
              </h2>
              <p className="text-xl text-slate-400">{content.whoShouldForm.subtitle}</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {content.whoShouldForm.categories.map((category, index) => (
                <div key={index} className="bg-[#111c17] border border-white/10 p-6 text-center">
                  <h3 className="font-semibold text-white mb-2">{category.title}</h3>
                  <p className="text-slate-400 text-sm">{category.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tax Information */}
      {content.taxInfo && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="service-tax-info">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Tax Information</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              {content.taxInfo.title}
            </h2>
            <p className="text-xl text-slate-400 mb-8">{content.taxInfo.subtitle}</p>
            <p className="text-slate-400 leading-relaxed mb-8">{content.taxInfo.content}</p>
            
            <div className="space-y-4">
              {content.taxInfo.options.map((option, index) => (
                <div key={index} className="bg-[#070d0b] border border-white/10 p-6">
                  <h3 className="font-semibold text-white mb-2">{option.title}</h3>
                  <p className="text-slate-400 mb-2">{option.description}</p>
                  <p className="text-sm text-slate-400"><span className="font-medium">Best for:</span> {option.bestFor}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ongoing Compliance */}
      {content.compliance && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="service-compliance">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Compliance</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                {content.compliance.title}
              </h2>
              <p className="text-xl text-slate-400">{content.compliance.subtitle}</p>
            </div>
            
            <div className="space-y-4">
              {content.compliance.requirements.map((req, index) => (
                <div key={index} className="bg-[#111c17] border border-white/10 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-white mb-2">{req.title}</h3>
                      <p className="text-slate-400 text-sm">{req.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-white">{req.frequency}</p>
                      <p className="text-xs text-slate-400">Penalty: {req.penalty}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <FeesSection entityType={content.entityType || 'llc'} serviceSlug={slug} />

      {/* Filings Section */}
      {['llc', 'c-corp', 's-corp', 'partnership'].includes(slug) && (
        <FilingsSection entityType={content.entityType || 'general'} />
      )}

      {/* FAQs Section */}
      {content.faqs && content.faqs.length > 0 && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="service-faqs">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">FAQs</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-slate-400">Common questions about {service.name}</p>
            </div>
            
            <FAQAccordion faqs={content.faqs} />
          </div>
        </section>
      )}

      {/* Related Services */}
      {content.relatedServices && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="service-related">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-12">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Related Services</p>
              <h2 className="font-serif text-4xl font-semibold text-white tracking-tight">
                You May Also Need
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {content.relatedServices.map((related, index) => (
                <Link key={index} to={`/usa/${related.slug}/`} className="bg-[#070d0b] border border-white/10 p-8 hover:shadow-card-hover transition-all group">
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">{related.name}</h3>
                  <p className="text-slate-400 text-sm">{related.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#1e293b]" data-testid="service-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <h2 className="text-3xl md:text-4xl text-white tracking-tight mb-6">
            Ready to Form Your {service.name}?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            From ${service.base_price}. {service.timeline_days || '3-10 business days'} timeline. Expert support included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isComingSoon ? (
              <span className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500/15 text-emerald-400 font-medium cursor-not-allowed">
                Coming Soon
              </span>
            ) : (
              <a href={APP_URL} className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors">
                Start {service.name} <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            )}
            <a href="mailto:hi@starteaseai.com" className="inline-flex items-center justify-center px-8 py-4 bg-slate-700 text-white font-medium hover:bg-slate-600 transition-colors">
              Talk to an Expert
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USAServicePage;
